import React, {useEffect, useState, useRef} from 'react';
import {Col, Row, Nav} from 'react-bootstrap';
import {InlineAlert} from '@/components/alerts';
import Button from '@/components/ui/Button';
import PaginationComponent from '@/components/pagination/PaginationComponent';

import {useStoreActions, useStoreState} from 'easy-peasy';
import axios, {AxiosResponse} from 'axios';

import AsyncSelect from 'react-select/async';
import Select, {components, ActionMeta, SingleValue, MultiValue, MenuListProps, GroupBase, InputActionMeta} from 'react-select';

import AwesomeDebouncePromise from 'awesome-debounce-promise';
import memoize from 'memoize-one';
import Form from 'react-bootstrap/Form';
import {ALL_EVENT_TYPES} from 'utils/eventConstants';
import {Link} from '@reach/router';
import {buildEventDetailURL} from '@/utils/urlBuilder';
import LoadingWrapper from '@/components/loading/LoadingWrapper';
import useData from '@/utils/useData';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import TableComponent from '@/components/table/Table';
import i18n from 'i18n';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';
import {UserDetails} from 'entities/user';
import {StoreModel} from '@/store';
import Icon from '@/components/icons/Icon';
import {hasPermission} from '@/utils/permissions/permissionDebug';
import {isFieldRestricted} from '@/utils/fieldRestrictions';
import {RestrictedFieldIndicator, RestrictedFieldInput} from '@/components/utils/DataNotAvailable';


interface Agent {
    id: number;
    name: string;
    externalId: string;
}

interface Team {
    id: number;
    name: string;
}

interface AssigneeOption {
    label: string;
    value: number;
    aType: 'agent' | 'team';
}

interface TaskStatusChoice {
    value: string;
    label: string;
}

interface Task {
    id: number;
    smallActionDescription: string;
    assignee: string;
    title: string;
    status: 'pending' | 'assigned' | 'in_progress' | 'resolved' | 'ignored' | 'rejected' | 'timed_out';
    assigneeAcronym: string;
    eventId: number;
    actionDescription: string;
    actionTitle: string;
    createdBy?: {
        name?: string;
        username?: string;
        userDisplayName: string;
        id: number;
    };
    eventType: typeof ALL_EVENT_TYPES[number];
    agentId?: number;
    teamId?: number;
    lastUpdated: string;
    statusLabel: string;
    createdOn: string;
}

interface ApiResponse<T> {
    data: T;
}

interface TasksResponse {
    tasks: Task[];
    pagesList: number[];
}

interface AgentsResponse {
    items: Agent[];
}

interface TeamsResponse {
    items: Team[];
}

const loadAgentsRequest = (inputValue: string): Promise<AssigneeOption[]> =>
    axios.get<AgentsResponse>(window.URLS.agentsResource, {
        params: {
            query: inputValue,
        },
    })
        .catch((error) => {
            if (error.response?.status === 400) {
                return error.response;
            }
            throw error;
        }).then((response: AxiosResponse<AgentsResponse>) =>
            response.data.items.map((x) => ({
                label: `${x.name} (${x.externalId})`,
                value: x.id,
                aType: 'agent' as const,
            }))
        );

const loadTeamsRequest = (inputValue: string): Promise<AssigneeOption[]> =>
    axios.get<TeamsResponse>(window.URLS.teamsResource, {
        params: {
            query: inputValue,
        },
    })
        .catch((error) => {
            if (error.response?.status === 400) {
                return error.response;
            }
            throw error;
        }).then((response: AxiosResponse<TeamsResponse>) =>
            response.data.items.map((x) => ({
                label: x.name,
                value: x.id,
                aType: 'team' as const,
            }))
        );

const loadAssigneesRequest = (inputValue: string): Promise<AssigneeOption[][]> =>
    axios.all([loadAgentsRequest(inputValue), loadTeamsRequest(inputValue)]);

const loadAssigneesRequestDebounced = AwesomeDebouncePromise(loadAssigneesRequest, 280);
const loadTeamsRequestDebounced = AwesomeDebouncePromise(loadTeamsRequest, 280);

const getAssigneeOptions = memoize(
    async (inputValue: string): Promise<AssigneeOption[]> => {
        const [agents, teams] = await loadAssigneesRequestDebounced(inputValue);
        return [...agents, ...teams];
    },
    (a: [string], b: [string]) => a[0] === b[0],
);

const getTeamOptions = memoize(
    async (inputValue: string): Promise<AssigneeOption[]> => {
        const teams = await loadTeamsRequestDebounced(inputValue);
        return [...teams];
    },
    (a: [string], b: [string]) => a[0] === b[0],
);

const AssignmentFilter = React.memo(() => {
    const storeSelected = useStoreActions((actions: StoreModel) => actions.tasks.loadSelected);
    const storeExpanded = useStoreActions((actions: StoreModel) => actions.tasks.loadExpanded);
    const {userData} = useOptimizedConfig();
    const restrictedFieldKeys: string[] = userData?.restrictedFieldKeys || [];
    const agentRestricted = isFieldRestricted('agent_pii', restrictedFieldKeys);
    const teamRestricted = isFieldRestricted('team_pii', restrictedFieldKeys);

    const [taskStatusChoices] = useData<TaskStatusChoice[]>(window.URLS.taskStatusChoicesResource, {
        storeResolver: 'tasks',
        actionResolver: 'loadTaskStatusChoices',
        dataResolver: 'taskStatusChoices',
    });

    const selectFilterAssigneesSelected = useStoreActions((actions: StoreModel) => actions.tasks.selectFilterAssigneeSelected);
    const filterAssignees = useStoreState((state: StoreModel) => state.tasks.filterAssigneeSelected);

    const selectFilterTeamsSelected = useStoreActions((actions: StoreModel) => actions.tasks.selectFilterTeamsSelected);

    const selectTaskStatusChoicesSelected = useStoreActions(
        (actions: StoreModel) => actions.tasks.selectTaskStatusChoicesSelected,
    );
    const taskStatusChoicesSelected = useStoreState((state: StoreModel) => state.tasks.taskStatusChoicesSelected);

    const storeSearchCurrentPage = useStoreActions((actions: StoreModel) => actions.tasks.setSearchCurrentPage);

    const isAssigneeOptionSelected = (option: AssigneeOption): boolean => {
        const selectedTeams = filterAssignees.filter((x: any) => x.aType === 'team');
        const selectedAgents = filterAssignees.filter((x: any) => x.aType === 'agent');

        return (
            (option.aType === 'agent' && selectedAgents.includes(option.value))
            || (option.aType === 'team' && selectedTeams.includes(option.value))
        );
    };

    const onFilterChange = (): void => {
        storeSelected([]);
        storeExpanded([]);
        storeSearchCurrentPage(1);
    };

    const submitTaskStatus = async (event: MultiValue<TaskStatusChoice> | null): Promise<void> => {
        selectTaskStatusChoicesSelected(event ? Array.from(event) : null);
        onFilterChange();
    };

    const submitTeamFilter = async (event: MultiValue<AssigneeOption> | null): Promise<void> => {
        selectFilterTeamsSelected(event ? event.map(item => item.value) : null);
        onFilterChange();
    };

    const submitAssigneeFilter = async (event: MultiValue<AssigneeOption> | null): Promise<void> => {
        selectFilterAssigneesSelected(event ? Array.from(event) : null);
        onFilterChange();
    };

    return (
        <Form noValidate>
            <div className="form-row">
                <Col xs={4}>
                    <Form.Group className="mb-3" controlId="assignee" >
                        <Form.Label>{i18n.t('jt_assignee.text')}</Form.Label>
                        {agentRestricted && teamRestricted ? (
                            <RestrictedFieldInput />
                        ) : (
                            <AsyncSelect<AssigneeOption, true>
                                isMulti
                                isClearable
                                cacheOptions
                                closeMenuOnSelect={false}
                                captureMenuScroll={false}
                                defaultOptions
                                loadOptions={getAssigneeOptions}
                                hideSelectedOptions={false}
                                onChange={submitAssigneeFilter}
                                isOptionSelected={isAssigneeOptionSelected}
                                styles={{
                                    valueContainer: (provided) => ({
                                        ...provided,
                                        maxHeight: '50px',
                                        overflowY: 'scroll',
                                        scrollbarWidth: 'none',
                                        overflowStyle: 'none',
                                        '&::-webkit-scrollbar': {
                                            width: 0,
                                            height: 0,
                                        },
                                    }),
                                }}
                            />
                        )}
                    </Form.Group>
                </Col>
                <Col xs={4}>
                    <Form.Group className="mb-3" controlId="team" >
                        <Form.Label>{i18n.t('jt_team.text')}</Form.Label>
                        {teamRestricted ? (
                            <RestrictedFieldInput />
                        ) : (
                            <AsyncSelect<AssigneeOption, true>
                                isMulti
                                isClearable
                                cacheOptions
                                closeMenuOnSelect={false}
                                captureMenuScroll={false}
                                defaultOptions
                                loadOptions={getTeamOptions}
                                hideSelectedOptions={false}
                                onChange={submitTeamFilter}
                                styles={{
                                    valueContainer: (provided) => ({
                                        ...provided,
                                        maxHeight: '50px',
                                        overflowY: 'scroll',
                                        scrollbarWidth: 'none',
                                        overflowStyle: 'none',
                                        '&::-webkit-scrollbar': {
                                            width: 0,
                                            height: 0,
                                        },
                                    }),
                                }}
                            />
                        )}
                    </Form.Group>
                </Col>
                <Col xs={4}>
                    <Form.Group className="mb-3" controlId="status" >
                        <Form.Label>{i18n.t('jt_status.text')}</Form.Label>
                        <Select<TaskStatusChoice, true>
                            isMulti
                            isClearable
                            isSearchable
                            options={taskStatusChoices}
                            closeMenuOnSelect={false}
                            onChange={submitTaskStatus}
                            hideSelectedOptions={false}
                            value={taskStatusChoicesSelected}
                            styles={{
                                valueContainer: (provided) => ({
                                    ...provided,
                                    maxHeight: '50px',
                                    overflowY: 'scroll',
                                    scrollbarWidth: 'none',
                                    overflowStyle: 'none',
                                    '&::-webkit-scrollbar': {
                                        width: 0,
                                        height: 0,
                                    },
                                }),
                            }}
                        />
                    </Form.Group>
                </Col >
            </div >
        </Form >
    );
});

interface AssigneeSelectProps {
    row: Task;
}

const AssigneeSelect = React.memo<AssigneeSelectProps>(({row}) => {
    const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false);

    const filterAssignees = useStoreState((state: StoreModel) => state.tasks.filterAssigneeSelected);
    const filterTeams = useStoreState((state: StoreModel) => state.tasks.filterTeamsSelected);
    const storeTasks = useStoreActions((actions: StoreModel) => actions.tasks.loadTasks);
    const taskStatusChoicesSelected = useStoreState((state: StoreModel) => state.tasks.taskStatusChoicesSelected);

    const assigneeType: 'agent' | 'team' = row.agentId ? 'agent' : 'team';
    const assigneeId: number = row.agentId || row.teamId || 0;

    const submitAssignee = ({value, aType}: AssigneeOption): void => {
        if (value) {
            let url = window.URLS.tasksResource;
            url = `${url}/${row.id}`;

            axios.put<TasksResponse>(url, {
                assignee_type: aType,
                assignee_id: value,
                params: {
                    selectedTaskStatusChoicesList: JSON.stringify(taskStatusChoicesSelected),
                    filterAssigneeList: JSON.stringify(filterAssignees),
                    filterTeamsList: JSON.stringify(filterTeams),
                },
            })
                .catch((error) => {
                    throw error;
                })
                .then((response: AxiosResponse<TasksResponse>) => {
                    storeTasks(response.data.tasks);
                });

            setMenuIsOpen(false);
        }
    };

    const toggleMenu = (e: React.MouseEvent): void => {
        e.stopPropagation();
        setMenuIsOpen((prev) => !prev);
    };

    const isAssigneeOptionSelected = (option: AssigneeOption): boolean =>
        option.aType === assigneeType && option.value === assigneeId;

    const MenuList = (props: MenuListProps<AssigneeOption, false, GroupBase<AssigneeOption>>) => {
        const {selectProps, children} = props;
        const {onInputChange, inputValue} = selectProps;

        return (
            <div onClick={(e) => e.stopPropagation()}>
                <Form.Control
                    type="text"
                    name="searchQuery"
                    autoComplete="off"
                    placeholder={i18n.t('es_search_for_teams_and_agents.text')}
                    value={inputValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onInputChange(e.target.value, {action: 'input-change', prevInputValue: inputValue})}
                    onBlur={() => setMenuIsOpen(false)}
                    autoFocus
                />
                <components.MenuList {...props}>
                    {children}
                </components.MenuList>
            </div>
        );
    };

    return (
        <AsyncSelect<AssigneeOption, false>
            isClearable={false}
            cacheOptions
            captureMenuScroll={false}
            menuIsOpen={menuIsOpen}
            value={{
                label: row.assignee,
                value: assigneeId,
                aType: assigneeType,
            }}
            defaultOptions
            loadOptions={getAssigneeOptions}
            hideSelectedOptions={false}
            onChange={(option: SingleValue<AssigneeOption>) => option && submitAssignee(option)}
            isOptionSelected={isAssigneeOptionSelected}
            components={{
                Control: () => {
                    return (
                        <div className="d-flex align-items-center">
                            <span className="mr-2">{row.assignee}</span>
                            <Button
                                variant="link"
                                size="sm"
                                className="plain-button p-0"
                                onClick={toggleMenu}
                                style={{
                                    padding: '2px 4px',
                                    minWidth: 'auto',
                                    lineHeight: 1,
                                }}
                                data-cy="assignments-list-link-btn-1">
                                <Icon name="mdi-pencil" size="xs" />
                            </Button>
                        </div>
                    );
                },
                MenuList,
            }}
        />
    );
});

const tasksUpdateRequest = (url: string, paramsDict: Record<string, any>): Promise<AxiosResponse<TasksResponse>> =>
    axios.get<TasksResponse>(url, {params: paramsDict})
        .catch((error) => {
            if (error.response?.status === 400) {
                return error.response;
            }
            throw error;
        });

const tasksUpdateRequestDebounced = AwesomeDebouncePromise(tasksUpdateRequest, 350);

interface IndeterminateCheckboxProps {
    indeterminate?: boolean;
    className?: string;
    checked?: boolean;
    disabled?: boolean;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const IndeterminateCheckbox = React.memo<IndeterminateCheckboxProps>(({indeterminate, className = '', ...rest}) => {
    const ref = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (ref.current && typeof indeterminate === 'boolean') {
            ref.current.indeterminate = !rest.checked && indeterminate;
        }
    }, [ref, indeterminate, rest.checked]);

    return (
        <input
            type="checkbox"
            ref={ref}
            className={`${className} cursor-pointer`}
            {...rest}
        />
    );
});

IndeterminateCheckbox.displayName = 'IndeterminateCheckbox';

const AssignmentsListView = React.memo(() => {
    const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();
    const {userData, loading: loadingUserData} = useOptimizedConfig();
    const restrictedFieldKeys: string[] = userData?.restrictedFieldKeys || [];
    const assigneeRestricted = isFieldRestricted('agent_pii', restrictedFieldKeys) && isFieldRestricted('team_pii', restrictedFieldKeys);

    const tasks = useStoreState((state: StoreModel) => state.tasks.items);
    const storeTasks = useStoreActions((actions: StoreModel) => actions.tasks.loadTasks);

    const storeSelected = useStoreActions((actions: StoreModel) => actions.tasks.loadSelected);
    const select = useStoreActions((actions: StoreModel) => actions.tasks.select);
    const deSelect = useStoreActions((actions: StoreModel) => actions.tasks.deSelect);
    const expand = useStoreActions((actions: StoreModel) => actions.tasks.expand);

    const selected = useStoreState((state: StoreModel) => state.tasks.selected);
    const expanded = useStoreState((state: StoreModel) => state.tasks.expanded);

    const searchPagesList = useStoreState((state: StoreModel) => state.tasks.searchPagesList);
    const storeSearchPagesList = useStoreActions((actions: StoreModel) => actions.tasks.setSearchPagesList);

    const searchCurrentPage = useStoreState((state: StoreModel) => state.tasks.searchCurrentPage);
    const storeSearchCurrentPage = useStoreActions((actions: StoreModel) => actions.tasks.setSearchCurrentPage);

    const taskStatusChoicesSelected = useStoreState((state: StoreModel) => state.tasks.taskStatusChoicesSelected);

    const filterAssignees = useStoreState((state: StoreModel) => state.tasks.filterAssigneeSelected);

    const filterTeams = useStoreState((state: StoreModel) => state.tasks.filterTeamsSelected);

    const loadingAssignments = useStoreState((state: StoreModel) => state.tasks.loading);
    const setLoadingAssignments = useStoreActions((actions: StoreModel) => actions.tasks.setLoading);

    useEffect(() => {
        const fetchData = async (): Promise<void> => {
            setLoadingAssignments(true);

            const paramsDict = {
                selectedTaskStatusChoicesList: JSON.stringify(taskStatusChoicesSelected),
                filterAssigneeList: JSON.stringify(filterAssignees),
                filterTeamsList: JSON.stringify(filterTeams),
                page: searchCurrentPage,
            };

            const response = await tasksUpdateRequestDebounced(window.URLS.tasksResource, paramsDict);

            storeTasks(response.data.tasks);
            storeSearchPagesList(response.data.pagesList);
            storeSelected([]);
            setLoadingAssignments(false);
        };
        fetchData();
    }, [
        taskStatusChoicesSelected,
        filterAssignees,
        filterTeams,
        searchCurrentPage,
        storeSearchCurrentPage,
        storeSearchPagesList,
        storeTasks,
        setLoadingAssignments,
        storeSelected,
    ]);

    const userCanAccessTasksResource = hasPermission('fs.assignment_task.list', userData?.permissions, true);
    if (!userCanAccessTasksResource && !loadingUserData) {
        return (
            <>
                <Row>
                    <Col lg={12}>
                        <Nav variant="pills" role="tablist" className="bg-white mb-3">
                            <Nav.Item>
                                <Link
                                    to="../assigned/"
                                    className="nav-link active"
                                >
                                    {i18n.t('jt_tasks.text')}
                                </Link>
                            </Nav.Item>
                        </Nav>
                    </Col>
                </Row>

                {
                    window.SCONSTANTS.sessionAgent !== 'true' ? (
                        <HelpTextBlock helpText={i18n.t('ht_the_tasks.text')} />
                    ) : null
                }

                {
                    window.SCONSTANTS.sessionAgent === 'true' ? (
                        <HelpTextBlock helpText={i18n.t('ht_this_view_assigned_to_you.text')} />
                    ) : null
                }

                <div className="alert alert-danger" role="alert">{i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    const pageChanged = (e: React.MouseEvent<HTMLElement>): void => {
        const target = e.target as HTMLElement;
        if (target.textContent) {
            storeSearchCurrentPage(parseInt(target.textContent, 10));
        }
    };

    const handlePageChange = (page: number) => {
        storeSearchCurrentPage(page);
    };

    const eventTypeIconMap: Record<string, React.ReactNode> = {
        call: <span>{i18n.t('jt_call.text')}</span>,
        email: <span>{i18n.t('jt_email.text')}</span>,
        chat: <span>{i18n.t('jt_chat.text')}</span>,
        feedback: <span>{i18n.t('jt_feedback.text')}</span>,
        comment: <span>{i18n.t('jt_comment.text')}</span>,
    };

    const statusStyleMap: Record<string, string> = {
        pending: 'label label-primary',
        assigned: 'label label-primary',
        in_progress: 'label label-info',
        resolved: 'label label-success',
        ignored: 'label label-warning',
        rejected: 'label label-danger',
        timed_out: 'label label-warning',
    };

    const buttonSubmit = async (status: string): Promise<void> => {
        const response = await axios.post<TasksResponse>(window.URLS.tasksResource, {
            statusName: status,
            selectedList: selected,
            params: {
                selectedTaskStatusChoicesList: JSON.stringify(taskStatusChoicesSelected),
                filterAssigneeList: JSON.stringify(filterAssignees),
                filterTeamsList: JSON.stringify(filterTeams),
            },
        });

        storeTasks(response.data.tasks);
        storeSearchPagesList(response.data.pagesList);
        storeSelected([]);
    };

    const handleBtnClick = (row: {id: number}): void => {
        expand(row.id);
    };

    const handleSelectAll = (): void => {
        if (tasks && tasks.length > 0) {
            const allTaskIds = tasks.map((task: Task) => task.id);
            const areAllSelected = allTaskIds.every((id: number) => selected.includes(id));

            if (areAllSelected) {
                allTaskIds.forEach((id: number) => deSelect(id));
            } else {
                allTaskIds.forEach((id: number) => select(id));
            }
        }
    };

    const handleRowSelect = (taskId: number): void => {
        if (selected.includes(taskId)) {
            deSelect(taskId);
        } else {
            select(taskId);
        }
    };

    const allSelected = tasks && tasks.length > 0 && tasks.every((task: Task) => selected.includes(task.id));
    const someSelected = tasks && tasks.length > 0 && tasks.some((task: Task) => selected.includes(task.id)) && !allSelected;

    const columns = [
        {
            id: 'select',
            header: () => (
                <IndeterminateCheckbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={handleSelectAll}
                />
            ),
            cell: (cell: any) => (
                <div className="px-1">
                    <IndeterminateCheckbox
                        checked={selected.includes(cell.row.original.id)}
                        onChange={() => handleRowSelect(cell.row.original.id)}
                    />
                </div>
            ),
        },
        {
            accessorKey: 'id',
            header: i18n.t('th_text.text'),
        },
        {
            accessorKey: 'actionTitle',
            header: i18n.t('th_name.text'),
            cell: (cell: any) => (
                <div>
                    <a
                        href={buildEventDetailURL(cell.row.original.eventId)}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {`${(cell.row.original.actionTitle)} `}
                        <small
                            className="text-secondary"
                        >
                            <em>
                                {eventTypeIconMap[cell.row.original.eventType]}
                                ,{' '}
                                {i18n.t('event_id.text')}
                                {cell.row.original.eventId}
                            </em>
                        </small>

                    </a>

                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="d-inline"
                    >
                        <div
                            dangerouslySetInnerHTML={{__html: cell.row.original.smallActionDescription}}
                        />
                    </div>
                </div>
            ),
        },
        {
            accessorKey: 'createdOn',
            header: i18n.t('th_date_created.text'),
            cell: (cell: any) => {
                return configLoaded ? formatDateTime(cell.row.original.createdOn, true, {setZone: false},
                ) : '';
            },
        },
        {
            accessorKey: 'assignee',
            header: i18n.t('th_assignee.text'),
            cell: (cell: any) => assigneeRestricted
                ? <RestrictedFieldIndicator />
                : <AssigneeSelect row={cell.row.original} />,
        },
        {
            accessorKey: 'statusLabel',
            header: i18n.t('th_status.text'),
            cell: (cell: any) => (
                <div>
                    <span className={statusStyleMap[cell.row.original.status]}>
                        {(cell.row.original.statusLabel)}
                    </span>
                </div>
            ),
        },
    ];

    const tableContent = (
        <LoadingWrapper
            loading={loadingAssignments}
            loadingText={i18n.t('jt_loading_data_general.text')}
            minHeight="400px"
        >
            <div className="card">
                <div className="card-body">
                    {tasks === undefined ? (
                        <InlineAlert
                            variant="error"
                            message={i18n.t('jt_invalid_search.text')}
                        />
                    ) : tasks.length === 0 ? (
                        <InlineAlert
                            variant="warning"
                            message={i18n.t('jt_results_found.text')}
                        />
                    ) : (
                        <>
                            <TableComponent data={tasks} columns={columns} />
                            <PaginationComponent
                                pages={searchPagesList}
                                currentPage={searchCurrentPage}
                                onPageChange={handlePageChange}
                            />
                        </>
                    )}
                </div>
            </div>
        </LoadingWrapper>
    );

    const ButtonsGroup = React.memo(() => (
        <Form noValidate>
            <Row>
                <Col xs={6}>
                    <div className="mb-2">
                        <span className="form-label">{i18n.t('jt_action.text')}</span>
                    </div>
                    <div className="d-flex flex-wrap mb-2">
                        <Button
                            variant="success"
                            size="sm"
                            className="mr-2"
                            onClick={() => buttonSubmit('resolved')}
                        >
                            <Icon name="mdi-check" size="sm" className="mr-2" />
                            {i18n.t('jt_resolve.text')}
                        </Button>
                        <Button
                            variant="warning"
                            size="sm"
                            className="mr-2"
                            onClick={() => buttonSubmit('ignored')}
                        >
                            <Icon name="mdi-cancel" size="sm" className="mr-2" />
                            {i18n.t('jt_ignore.text')}
                        </Button>
                        <Button
                            variant="danger"
                            size="sm"
                            className="mr-4 mb-2"
                            onClick={() => buttonSubmit('rejected')}
                        >
                            <Icon name="mdi-close" size="sm" className="mr-2" />
                            {i18n.t('jt_reject.text')}
                        </Button>
                    </div>
                </Col>
            </Row>
        </Form>
    ));

    return (
        <>
            {
                window.SCONSTANTS.sessionAgent !== 'true' ? (
                    <HelpTextBlock helpText={i18n.t('ht_the_tasks.text')} />
                ) : null
            }

            {
                window.SCONSTANTS.sessionAgent === 'true' ? (
                    <HelpTextBlock helpText={i18n.t('ht_this_view_assigned_to_you.text')} />
                ) : null
            }

            <Row className="events-table-header">
                <Col xs={6}>
                    <ButtonsGroup />
                </Col>
                <Col xs={6}>
                    <AssignmentFilter />
                </Col>
            </Row>

            {tableContent}

        </>
    );
});

export default AssignmentsListView;
